/**
 * home_layout.ts — WHERE a versona lives inside their dwelling (PURE — no
 * three, no DOM; node-testable).
 *
 * The server keeps versonas at a household-shared home ANCHOR (families under
 * one roof — spreadSettlementLife's family law); the client's life layer calls
 * `interiorHomeSpot` to seat/bed each resident on a real spot INSIDE the
 * enterable building the anchor falls in: sleepers on the floor-1 bed (and
 * bedrolls beside it), children playing around the living-room rug, resters in
 * the sofa conversation grouping. Deterministic per (versona, building, state)
 * so every client agrees and nothing jitters frame to frame.
 *
 * POSITION MIRROR: the bed / sofa / table / rug coordinates below mirror the
 * FIXED slot positions in BuildingInteriorModule.furnishVersonaHome's planFor
 * (only the furniture MODEL picks are random there, never the positions) —
 * change one, change both.
 */

export interface HomeSpecLike {
  floors: number;
  hw: number;
  hd: number;
  stair: { x: number; z: number; w: number; d: number };
}

export interface HomeSpot {
  /** Building-local coords (the enterable's frame; toWorld rotates by b.yaw). */
  lx: number;
  lz: number;
  /** Which storey (slab y = floor === 0 ? 0.08 : floor * 3.4). */
  floor: number;
  /** Local yaw the body faces (add b.yaw for world). */
  yaw: number;
  /** Lift above the slab (bed height, seat height, bedroll pad). */
  lift: number;
  /** Reclined pose (sleeping on a bed/bedroll). */
  lying: boolean;
}

/** FNV-1a over a string with a murmur-style finalizer (the HASH LAW: bare
 *  FNV-1a is last-character-weak — consecutive ids cluster without it). */
function h01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Keep a spot inside the walkable core, out of the door corridor and the
 *  stair zone (mirrors furnishVersonaHome's clearOf). */
function legal(spec: HomeSpecLike, lx: number, lz: number): boolean {
  if (Math.abs(lx) < 1.4 && lz < -spec.hd + 3.4) return false; // door corridor (door is on -Z)
  const st = spec.stair;
  if (lx > st.x - 2.0 && lx < st.x + 2.0 && lz > st.z - st.d / 2 - 1.8 && lz < st.z + st.d / 2 + 1.8) return false;
  return Math.abs(lx) < spec.hw - 0.6 && Math.abs(lz) < spec.hd - 0.6;
}

/** Nudge an illegal spot toward the room center until it clears. */
function settle(spec: HomeSpecLike, lx: number, lz: number): { lx: number; lz: number } {
  let x = lx, z = lz;
  for (let i = 0; i < 6 && !legal(spec, x, z); i++) { x *= 0.72; z = z * 0.72 + 0.35; }
  return legal(spec, x, z) ? { lx: x, lz: z } : { lx: 0, lz: Math.min(spec.hd - 1, 1.2) };
}

/**
 * The deterministic interior spot for one resident in one home state.
 * `state`: 'sleeping' → the bed (first sleeper) / bedrolls (the rest);
 * 'playing' → around the living-room rug; anything else ('resting',
 * 'chatting') → the sofa conversation grouping on the ground floor.
 */
export function interiorHomeSpot(versonaId: string, buildingId: string, spec: HomeSpecLike, state: string, room?: ResidentRoom | null): HomeSpot {
  if (room) return anchoredHomeSpot(versonaId, buildingId, room, spec, state); // the planned room (slice C): what the visit drew
  const hw = spec.hw, hd = spec.hd;
  const ha = h01(`${versonaId}|${buildingId}|a`);
  const hb = h01(`${versonaId}|${buildingId}|b`);

  if (state === 'sleeping') {
    // Sleeping floor: storey 1 when the home has one (the themed bedroom),
    // else the ground floor of a one-room cottage.
    const floor = spec.floors > 1 ? 1 : 0;
    const k = Math.floor(ha * 4); // which sleeping spot this resident claims
    if (k === 0) {
      // The bed (planFor f===1: bed at (1.7, hd-1.35), yaw π — head to +Z).
      const p = settle(spec, 1.7, hd - 1.7);
      return { lx: p.lx, lz: p.lz, floor, yaw: Math.PI, lift: 0.5, lying: true };
    }
    // Bedrolls fan along the -X side of the bedroom.
    const p = settle(spec, -1.0 - (k - 1) * 1.35, hd - 1.7 - hb * 0.8);
    return { lx: p.lx, lz: p.lz, floor, yaw: Math.PI + (hb - 0.5) * 0.5, lift: 0.14, lying: true };
  }

  if (state === 'playing') {
    // Children play around the living-room rug (planFor f===0: rug at
    // (hw-2.4, -hd*0.25)) — a small ring so siblings tumble together.
    const ang = ha * Math.PI * 2;
    const r = 0.9 + hb * 0.9;
    const p = settle(spec, hw - 2.4 + Math.cos(ang) * r, -hd * 0.25 + Math.sin(ang) * r);
    return { lx: p.lx, lz: p.lz, floor: 0, yaw: ang + Math.PI, lift: 0, lying: false };
  }

  // Resting / chatting: the conversation grouping — first resident ON the
  // sofa (planFor f===0: seating at (hw-1.0, -hd*0.25) facing -X), the rest
  // stand around the low table 1.4 m out.
  if (ha < 0.4) {
    const p = settle(spec, hw - 1.15, -hd * 0.25 + (hb - 0.5) * 1.2);
    return { lx: p.lx, lz: p.lz, floor: 0, yaw: -Math.PI / 2, lift: 0.26, lying: false };
  }
  const ang = hb * Math.PI * 2;
  const p = settle(spec, hw - 2.45 + Math.cos(ang) * 1.25, -hd * 0.25 + Math.sin(ang) * 1.25);
  // Face the table so the family reads as gathered, not scattered.
  return { lx: p.lx, lz: p.lz, floor: 0, yaw: Math.atan2((hw - 2.45) - p.lx, (-hd * 0.25) - p.lz), lift: 0, lying: false };
}

/** The HASH LAW's hash, for the deed-home picker (lib/deed_home.ts) — one hash for every home spot. */
export const homeHash01 = h01;

/** A resident with no furniture to take (an unfurnished deed, a state with no piece): a legal standing spot,
 *  deterministic per (versona, building), inside the walkable core. */
export function standingSpot(versonaId: string, buildingId: string, spec: HomeSpecLike): HomeSpot {
  const ha = h01(`${versonaId}|${buildingId}|s`), hb = h01(`${versonaId}|${buildingId}|t`);
  const p = settle(spec, (ha - 0.5) * (spec.hw - 1.6) * 1.6, (hb - 0.5) * (spec.hd - 1.6) * 1.2 + 0.4);
  return { lx: p.lx, lz: p.lz, floor: 0, yaw: hb * Math.PI * 2, lift: 0, lying: false };
}

// ─── RESIDENTS ON REAL ANCHORS (the interiors programme, slice C, 2026-10-01) ────────────────────────────────────────
//
// The visit furnishes a room from a planned DESCRIPTOR now (lib/interior_wiring.ts), so the fixed coordinates above no
// longer say where the bed is. `anchoredHomeSpot` seats a resident on what the room REALLY holds: a sleeper on a bed's
// socket, a rester on a seat's socket or at a standing point the planner proved free, a child by the rug. THE POSITION
// MIRROR (law 21) is now "the resident reads the room the visit drew" — one descriptor, both sides. `interiorHomeSpot`
// and `deedResidentSpot` take the room as an optional last argument; without it (the lever `atlas:interior-planner =
// 'off'`, or the plan not landed yet) they answer exactly as before.

/** A place a body sits or lies: building-local, the plans' yaw (the body faces the piece's front), the slab it is on. */
export interface ResidentSocket { pose: 'sit' | 'lie'; x: number; z: number; yaw: number; floor: number; lift: number; slot: string }
/** A free cell a body may stand on, and the way it faces. */
export interface ResidentStand { x: number; z: number; facing: number; floor: number }
/** What a room offers its residents (`interior_wiring.residentRoomOf` builds it from the descriptor and the seat view). */
export interface ResidentRoom {
  sockets: ResidentSocket[];
  stands: ResidentStand[];
  rugs: Array<{ x: number; z: number; floor: number }>;
  /** The drawn bounds of what blocks walking (per floor) — a child never plays inside a wardrobe. */
  solids: Array<{ floor: number; x0: number; x1: number; z0: number; z1: number }>;
}

/** The share of resting residents who take a seat when the room has one (the rest stand about — a room of people all
 *  seated reads as a waiting room). */
export const RESIDENT_SIT_SHARE = 0.6;

/**
 * The resident's spot in a PLANNED room. Deterministic per (versona, building, state); two residents may share a sofa
 * or a bed — a family does.
 *   sleeping → a bed's socket (lying); no bed → asleep in a seat (sitting — lying on a chair is a mime); neither →
 *              a bedroll on a free standing point, else the legacy standing spot.
 *   playing  → a ring round a rug, kept off every blocking piece; no rug → a free standing point.
 *   else     → a seat's socket (RESIDENT_SIT_SHARE of residents), else a free standing point facing what it stands by.
 */
export function anchoredHomeSpot(versonaId: string, buildingId: string, room: ResidentRoom, spec: HomeSpecLike, state: string): HomeSpot {
  const ha = h01(`${versonaId}|${buildingId}|a`), hb = h01(`${versonaId}|${buildingId}|b`);
  const pick = <T>(list: readonly T[], h: number): T => list[Math.floor(h * list.length) % list.length];
  const onSocket = (s: ResidentSocket, lying: boolean): HomeSpot => ({ lx: s.x, lz: s.z, floor: s.floor, yaw: s.yaw, lift: s.lift, lying });
  const onStand = (s: ResidentStand): HomeSpot => ({ lx: s.x, lz: s.z, floor: s.floor, yaw: s.facing, lift: 0, lying: false });
  const beds = room.sockets.filter((s) => s.pose === 'lie'), seats = room.sockets.filter((s) => s.pose === 'sit');
  const inSolid = (x: number, z: number, floor: number): boolean => room.solids.some((q) => q.floor === floor && x > q.x0 - 0.3 && x < q.x1 + 0.3 && z > q.z0 - 0.3 && z < q.z1 + 0.3);
  // THE LAST RESORT IS STILL A FREE SPOT: a room with no standing point of its own (a workshop's are all behind the
  // bench) falls back to the legacy hashed spot — which knows nothing of this room's furniture and landed INSIDE a
  // dining table in the first sweep. Walk a deterministic spiral out of it to the first legal cell clear of every
  // blocking piece; only a room packed wall to wall keeps the raw spot.
  const freeSpot = (): HomeSpot => {
    const base = standingSpot(versonaId, buildingId, spec);
    if (!inSolid(base.lx, base.lz, base.floor)) return base;
    for (let ring = 1; ring <= 8; ring++) for (let k = 0; k < 8; k++) {
      const ang = (hb + k / 8) * Math.PI * 2, r = ring * 0.45;
      const x = base.lx + Math.cos(ang) * r, z = base.lz + Math.sin(ang) * r;
      if (legal(spec, x, z) && !inSolid(x, z, base.floor)) return { ...base, lx: x, lz: z };
    }
    return base;
  };
  if (state === 'sleeping') {
    if (beds.length > 0) return onSocket(pick(beds, ha), true);
    if (seats.length > 0) return onSocket(pick(seats, ha), false);
    if (room.stands.length > 0) return { ...onStand(pick(room.stands, ha)), lying: true, lift: 0.14 };
    return { ...freeSpot(), lying: true, lift: 0.14 };
  }
  if (state === 'playing') {
    if (room.rugs.length > 0) {
      const rug = pick(room.rugs, ha);
      for (let k = 0; k < 8; k++) {
        const ang = (hb + k / 8) * Math.PI * 2, r = 0.8 + ha * 0.8;
        const x = rug.x + Math.cos(ang) * r, z = rug.z + Math.sin(ang) * r;
        if (!legal(spec, x, z) || inSolid(x, z, rug.floor)) continue;
        return { lx: x, lz: z, floor: rug.floor, yaw: Math.atan2(rug.x - x, rug.z - z), lift: 0, lying: false };
      }
    }
    if (room.stands.length > 0) return onStand(pick(room.stands, hb));
    return freeSpot();
  }
  if (seats.length > 0 && (ha < RESIDENT_SIT_SHARE || room.stands.length === 0)) return onSocket(pick(seats, hb), false);
  if (room.stands.length > 0) return onStand(pick(room.stands, hb));
  return freeSpot();
}

/** The states the life layer treats as AT-HOME (indoors when the anchor falls
 *  inside an enterable dwelling). 'chatting' is what 'resting' becomes when
 *  family is near — the arrival-company remap. */
export const HOME_STATES: ReadonlySet<string> = new Set(['resting', 'sleeping', 'playing', 'chatting']);
